'use client'

import type { ReactNode } from 'react'

import styled from 'styled-components'

import { colors } from '../../constants/colors'
import { breakpoints } from '../breakPoints'
import type { ProductListCardVariant } from './ProductListCard'

interface ProductListTag {
  id: number
  name?: string
}

interface ProductListHeaderProps {
  actions?: ReactNode
  activeTagId?: number
  onTagChange?: (tagId: number) => void
  onVariantChange: (variant: ProductListCardVariant) => void
  tags?: ProductListTag[]
  title: string
  variant: ProductListCardVariant
}

export function ProductListHeader({
  actions,
  activeTagId,
  onTagChange,
  onVariantChange,
  tags = [],
  title,
  variant,
}: ProductListHeaderProps) {
  return (
    <Header>
      <TitleRow>
        <Title>{title}</Title>
        <ViewToggle aria-label={'Вариант отображения товаров'}>
          <ViewButton
            $active={variant === 'row'}
            aria-label={'Показывать товары списком'}
            aria-pressed={variant === 'row'}
            onClick={() => onVariantChange('row')}
            type={'button'}
          >
            <ViewIcon $src={'/images/product-view-toggle/row.svg'} />
          </ViewButton>
          <ViewButton
            $active={variant === 'card'}
            aria-label={'Показывать товары карточками'}
            aria-pressed={variant === 'card'}
            onClick={() => onVariantChange('card')}
            type={'button'}
          >
            <ViewIcon $src={'/images/product-view-toggle/grid.svg'} />
          </ViewButton>
        </ViewToggle>
        {actions && <Actions>{actions}</Actions>}
      </TitleRow>

      {tags.length > 0 && (
        <Tags aria-label={'Фильтр по тегам'}>
          {tags.map(tag => (
            <TagButton
              $active={tag.id === activeTagId}
              key={tag.id}
              onClick={() => onTagChange?.(tag.id)}
              type={'button'}
            >
              {tag.name}
            </TagButton>
          ))}
        </Tags>
      )}
    </Header>
  )
}

const Header = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
  margin: 24px 0;
`

const TitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  width: 100%;
`

const Title = styled.h1`
  margin: 0;
  color: ${colors.black};
  font-size: 24px;
  font-weight: 700;
  line-height: 1.2;
`

const ViewToggle = styled.div`
  display: flex;
  align-items: center;
  overflow: hidden;
  border-radius: 12px;
  background: #f8f8fa;

  @media (width < ${breakpoints.productCardsDesktopMin}) {
    display: none;
  }
`

const ViewButton = styled.button<{ $active: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 54px;
  height: 34px;
  padding: 6px 16px;
  border: 0;
  border-radius: 12px;
  background: ${({ $active }) => ($active ? colors.primary : '#f8f8fa')};
  color: ${({ $active }) => ($active ? colors.white : '#a0a6a6')};
  cursor: pointer;
`

const ViewIcon = styled.span<{ $src: string }>`
  display: block;
  flex: 0 0 22px;
  width: 22px;
  height: 22px;
  background: currentColor;
  mask: ${({ $src }) => `url(${$src}) center / contain no-repeat`};
  -webkit-mask: ${({ $src }) => `url(${$src}) center / contain no-repeat`};
`

const Actions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-left: auto;
`

const Tags = styled.div`
  display: flex;
  gap: 4px;
  width: 100%;
  overflow-x: auto;
  padding-bottom: 2px;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }
`

const TagButton = styled.button<{ $active: boolean }>`
  flex: 0 0 auto;
  min-height: 36px;
  padding: 7px 16px;
  border: 0;
  border-radius: 12px;
  background: ${({ $active }) => ($active ? colors.primary : '#f8f8fa')};
  color: ${({ $active }) => ($active ? colors.white : '#a0a6a6')};
  font-size: 14px;
  font-weight: 500;
  line-height: 1.5;
  cursor: pointer;
`
